import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {HavingCountTable, Table} from '@tj/tj-dmp-lib-api';
import {InfoView} from '../../filter.type';
import {ConfirmationService, SelectItem} from 'primeng/api';
import {TranslateService} from '@ngx-translate/core';

@Component({
    selector: 'dmp-filter-having',
    templateUrl: './filter-having.component.html',
    styleUrls: ['./filter-having.component.scss'],
    providers: [ConfirmationService],
    standalone: false
})
export class FilterHavingComponent implements OnInit, OnChanges {


    constructor(private confirmationService: ConfirmationService,
                private readonly translate: TranslateService) {
        this.filterOptions = [
            { label: '=', value: 'EQUALS' },
            { label: this.translate.instant('components.filter-having.range'), value: 'RANGE' },
            { label: '>=', value: 'GREATER_THAN' },
            { label: '<=', value: 'LESS_THAN' },
            { label: this.translate.instant('common.None'), value: 'NONE' },
        ];
    }

    @Input() tables!: Table[];
    @Input() view!: InfoView;
    @Input() partner: string;

    // Tableau de filtres
    @Input() filters: HavingCountTable[] = [];
    @Output() filterChange = new EventEmitter<HavingCountTable[]>();
    public list: SelectItem<string>[] = [];
    public edit = false;

    // Configuration des conditions automatiques par table
    private AUTO_CONDITIONS = {};

    // Options disponibles pour les filtres
    public filterOptions: { label: string; value: string }[];

    ngOnInit(): void {
        this.AUTO_CONDITIONS = {
            'personal_newsletter': {
                'newsletter_registration_status': true,
                'id_affi': this.partner
            }
        };
    }

    // Ajoute un nouveau filtre
    public addFilter(): void {
        this.filters.push({
            tableName: null,
            operator: null as any,
            value1: 0,
            value2: 0,
            autoConditions: {}
        });
    }

    public onTableChange(filter: HavingCountTable): void {
        if (filter.tableName && this.AUTO_CONDITIONS[filter.tableName]) {
            filter.autoConditions = { ...this.AUTO_CONDITIONS[filter.tableName] };
        } else {
            filter.autoConditions = {};
        }
    }

    public addRootFilter(): void {
        this.edit = true;
        this.addFilter();
    }
    // Supprime le filtre à l'index donné
    public removeFilter(index: number): void {
        this.confirmationService.confirm({
            message: this.translate.instant('components.filter-having.confirmRemoveOne'),
            accept: () => {
                this.filters.splice(index, 1);
            }
        });
    }

    public removeAllFilter() {
        this.confirmationService.confirm({
            message: this.translate.instant('components.filter-having.confirmRemoveAll'),
            accept: () => {
                this.filters = [];
                this.doValidate();
            }
        });
    }

    // Méthode pour vérifier si la condition est 'between'
    public isBetweenCondition(filter: HavingCountTable): boolean {
        return filter.operator === 'RANGE';
    }

    // Méthode pour vérifier si la condition est 'greater' ou 'less'

    public isSingleValueCondition(filter: HavingCountTable): boolean {
        return filter.operator === 'GREATER_THAN' || filter.operator === 'LESS_THAN' || filter.operator === 'EQUALS';
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.updateList();
    }

    private updateList(): void {
        if (this.view.havingCountTable) {
            this.list = this.view.havingCountTable.map(table =>
                ({
                    value: table.code,
                    label: table.label || table.code
                } as SelectItem));
        } else {
            this.list = [];
        }
    }

    public isError(filter: HavingCountTable): boolean {
        if (!filter || !filter.tableName || filter.tableName.length === 0) {
            return true;
        }

        if (filter.operator === 'NONE') {
            return false;
        }

        if (['EQUALS', 'LESS_THAN', 'GREATER_THAN'].includes(filter.operator)) {
            return !(filter.value1 != null && filter.value1 >= 0);
        }

        if (filter.operator === 'RANGE') {
            return !(filter.value1 != null && filter.value2 != null && filter.value1 >= 0 && filter.value2 >= 0 && filter.value1 <= filter.value2); // Valide si les deux valeurs sont définies et value1 <= value2
        }
        return true;
    }

    public doValidate(): void {
        this.edit = false;
        this.filters = this.filters.filter(f => !this.isError(f));
        const cleanedFilters = this.filters.map(filter => ({
            ...filter,
            autoConditions: this.AUTO_CONDITIONS[filter.tableName] || {}
        }));
        this.filterChange.emit(cleanedFilters);
    }

    /** Libellé de l'opérateur tel que dans la liste de choix, comme le fait filter-display. */
    public getLabelOperator(operator: HavingCountTable.OperatorEnum): string {
        return this.filterOptions.find(f => f.value === operator)?.label ?? '';
    }

    /** Libellé de l'entité comptée ; le code de la table à défaut (entité absente de la vue). */
    public getLabelTable(tableName: string): string {
        return this.list.find(t => t.value === tableName)?.label ?? tableName;
    }
}
